import React, { useState } from 'react';
import { GenericColumn, isDateColumn } from '../utils/tableColumns';
import { DateRangeFilter, DatePresetId } from '../utils/datePresets';
import DateRangeFilterPopover from './DateRangeFilterPopover';
import { ChevronDown, ChevronUp, Search, X } from 'lucide-react';

// Define filter operation types
type StringFilterOperation = 'contains' | 'equals' | 'startsWith' | 'endsWith';

interface FilterPopoverProps<T> {
  column: GenericColumn<T>;
  data: T[];
  filterState: { [key: string]: string[] };
  dateRangeFilters: { [key: string]: DateRangeFilter };
  filterPopoverRef: React.RefObject<HTMLDivElement>;
  setActiveFilter: (filter: string | null) => void;
  handleFilterChange: (columnKey: string, value: string) => void;
  handleDateRangeFilterChange: (columnKey: string, type: 'startDate' | 'endDate', value: string) => void;
  clearDateRangeFilter: (columnKey: string) => void;
  applyPresetDateRange: (columnKey: string, presetId: DatePresetId) => void;
}

const FilterPopover = <T,>({
  column,
  data,
  filterState,
  dateRangeFilters,
  filterPopoverRef,
  setActiveFilter,
  handleFilterChange,
  handleDateRangeFilterChange,
  clearDateRangeFilter,
  applyPresetDateRange
}: FilterPopoverProps<T>) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [advancedFilterOperation, setAdvancedFilterOperation] = useState<StringFilterOperation>('contains');
  const [advancedFilterValue, setAdvancedFilterValue] = useState('');

  if (!column.filterable || !column.filterOptions) return null;

  // Use DateRangeFilterPopover for date columns
  if (isDateColumn(column.key.toString(), column.isDateColumn)) {
    return <DateRangeFilterPopover
      column={column}
      filterPopoverRef={filterPopoverRef}
      dateRangeFilters={dateRangeFilters}
      handleDateRangeFilterChange={handleDateRangeFilterChange}
      clearDateRangeFilter={clearDateRangeFilter}
      applyPresetDateRange={applyPresetDateRange}
      setActiveFilter={setActiveFilter}
    />;
  }

  const options = column.filterOptions(data);
  const columnKey = column.key.toString();
  const activeFilters = filterState[columnKey] || [];

  // Filter options based on search term
  const filteredOptions = searchTerm
    ? options.filter(option =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : options;

  // Handle advanced filter application
  const applyAdvancedFilter = () => {
    if (!advancedFilterValue) return;

    // Format the filter value with the operation prefix
    const filterValue = `${advancedFilterOperation}:${advancedFilterValue}`;
    handleFilterChange(columnKey, filterValue);
    setAdvancedFilterValue('');
  };

  // Handle clearing all filters for this column
  const handleClearFilters = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveFilter(null);

    // Clear all active filters for this column
    activeFilters.forEach(filter => {
      handleFilterChange(columnKey, filter);
    });
  };

  return (
    <div
      ref={filterPopoverRef}
      className="absolute top-full left-0 mt-1 z-40 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 p-2 max-h-80 overflow-auto min-w-[250px]"
    >
      <div className="flex justify-between items-center mb-2 border-b pb-1">
        <span className="text-sm font-medium">กรองด้วย {column.label}</span>
        <button
          className="text-xs text-blue-600 hover:text-blue-800"
          onClick={handleClearFilters}
        >
          ล้าง
        </button>
      </div>

      {/* Search input */}
      <div className="relative mb-2">
        <div className="absolute inset-y-0 left-0 flex items-center pl-2 pointer-events-none">
          <Search size={14} className="text-gray-500" />
        </div>
        <input
          type="text"
          placeholder="ค้นหา..."
          className="pl-8 pr-4 py-1.5 w-full text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onClick={e => e.stopPropagation()}
        />
        {searchTerm && (
          <button
            className="absolute inset-y-0 right-0 flex items-center pr-2"
            onClick={(e) => {
              e.stopPropagation();
              setSearchTerm('');
            }}
          >
            <X size={14} className="text-gray-500" />
          </button>
        )}
      </div>

      {/* Advanced filter toggle */}
      <div className="mb-2">
        <button
          className="text-xs flex items-center text-blue-600 hover:text-blue-800"
          onClick={() => setShowAdvancedFilter(!showAdvancedFilter)}
        >
          ตัวกรองขั้นสูง
          {showAdvancedFilter ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      </div>

      {/* Advanced filter options */}
      {showAdvancedFilter && (
        <div className="mb-2 p-2 bg-gray-50 rounded-md">
          <div className="flex items-center space-x-2 mb-2">
            <select
              className="p-1 text-sm border border-gray-300 rounded-md flex-shrink-0"
              value={advancedFilterOperation}
              onChange={(e) => setAdvancedFilterOperation(e.target.value as StringFilterOperation)}
              onClick={e => e.stopPropagation()}
            >
              <option value="contains">มี</option>
              <option value="equals">เท่ากับ</option>
              <option value="startsWith">เริ่มด้วย</option>
              <option value="endsWith">ลงท้ายด้วย</option>
            </select>
            <input
              type="text"
              className="p-1 text-sm border border-gray-300 rounded-md flex-grow"
              value={advancedFilterValue}
              onChange={(e) => setAdvancedFilterValue(e.target.value)}
              placeholder="ระบุค่า..."
              onClick={e => e.stopPropagation()}
            />
          </div>
          <button
            className="w-full py-1 px-2 text-xs bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={(e) => {
              e.stopPropagation();
              applyAdvancedFilter();
            }}
            disabled={!advancedFilterValue}
          >
            เพิ่มตัวกรอง
          </button>
        </div>
      )}

      {/* Filter options list */}
      <div className="mt-2 space-y-1 max-h-40 overflow-y-scroll">
        {filteredOptions.map((option) => (
          <div key={option} className="flex items-center">
            <input
              type="checkbox"
              id={`${columnKey}-${option}`}
              checked={activeFilters.includes(option)}
              onChange={(e) => {
                e.stopPropagation();
                handleFilterChange(columnKey, option);
              }}
              onClick={(e) => e.stopPropagation()}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label
              htmlFor={`${columnKey}-${option}`}
              className="ml-2 block text-sm text-gray-900 truncate max-w-[200px]"
              title={option}
              onClick={(e) => {
                e.preventDefault();
                handleFilterChange(columnKey, option);
              }}
            >
              {option}
            </label>
          </div>
        ))}

        {filteredOptions.length === 0 && (
          <div className="text-center text-gray-500 text-sm py-2">
            ไม่พบตัวเลือก
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterPopover;
